import { Search, Settings } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Toggle } from "../ui/toggle";
import { TabNavigation, AssessmentTab } from "../molecules/TabNavigation";
import { FilterDropdown } from "../molecules/FilterDropdown";
import { DateRangeFilter } from "../molecules/DateRangeFilter";
import { ResidentsFilterDropdown } from "../molecules/ResidentsFilterDropdown";
import { ActiveFilters } from "../molecules/ActiveFilters";
import { ActiveResidentsFilters } from "../molecules/ActiveResidentsFilters";
import { FilterState } from "../../types/dashboard";
import type { ApiService } from "../../services/apiService";
import type { RiskLevel } from "../../types/residents";

interface HeaderBarProps {
  activeTab: AssessmentTab;
  onTabChange: (tab: AssessmentTab) => void;
  filters: FilterState;
  onFilterChange: (field: keyof FilterState, value: string | string[]) => void;
  onClearAllFilters: () => void;
  /** Apply all filters at once (prevents race conditions with debouncing) */
  onApplyAllFilters?: (filters: FilterState) => void;
  resultCount: number;
  showRecentlyComplete?: boolean;
  onShowRecentlyCompleteChange?: (checked: boolean) => void;
  /** Custom API service for authentication (used when passed from Dashboard) */
  api?: ApiService;
  // Residents tab filter props
  residentsSearch?: string;
  onResidentsSearchChange?: (value: string) => void;
  selectedRiskLevels?: RiskLevel[];
  onRiskLevelsChange?: (levels: RiskLevel[]) => void;
  daysSinceReview?: string;
  onDaysSinceReviewChange?: (value: string) => void;
  showHiddenResidents?: boolean;
  onShowHiddenChange?: (checked: boolean) => void;
  /** NETSOL-11791: "My Assignments" toggle state + handler */
  myAssignments?: boolean;
  onMyAssignmentsChange?: (checked: boolean) => void;
  /**
   * NETSOL-11843: whether the "My Assignments" feature is licensed/available.
   * The toggle is only rendered when this is true; defaults false so an
   * unlicensed facility never sees it.
   */
  myAssignmentsAvailable?: boolean;
  /**
   * NETSOL-11790: when true, a provider security setting forces the toggle on
   * and locks it (the queue is restricted to assigned residents).
   */
  myAssignmentsLocked?: boolean;
  onSettingsClick?: () => void;
  selectedFacilityId?: string;
  onFacilityChange?: (facilityId: string) => void;
  facilityOptions?: { id: string; name: string }[];
  selectedBuilding?: string;
  onBuildingChange?: (value: string) => void;
  buildingOptions?: { value: string; label: string }[];
  selectedStation?: string[];
  onStationChange?: (value: string[]) => void;
  stationOptions?: { value: string; label: string }[];
  /** When true, show facility filter on all tabs (multi-facility mode) */
  multiFacilityMode?: boolean;
  /** Tabs visible based on user permissions */
  visibleTabs?: AssessmentTab[];
}

export function HeaderBar({
  activeTab,
  onTabChange,
  filters,
  onFilterChange,
  onClearAllFilters,
  onApplyAllFilters,
  resultCount,
  showRecentlyComplete,
  onShowRecentlyCompleteChange,
  api,
  residentsSearch,
  onResidentsSearchChange,
  selectedRiskLevels,
  onRiskLevelsChange,
  daysSinceReview,
  onDaysSinceReviewChange,
  showHiddenResidents,
  onShowHiddenChange,
  myAssignments,
  onMyAssignmentsChange,
  myAssignmentsAvailable = false,
  myAssignmentsLocked = false,
  onSettingsClick,
  selectedFacilityId,
  onFacilityChange,
  facilityOptions,
  selectedBuilding,
  onBuildingChange,
  buildingOptions = [],
  selectedStation,
  onStationChange,
  stationOptions = [],
  multiFacilityMode = false,
  visibleTabs,
}: HeaderBarProps) {
  return (
    <div className="w-full flex items-center gap-2 flex-wrap p-0 relative">
      {/* Tab Navigation */}
      <div className="flex overflow-hidden shrink-0">
        <TabNavigation activeTab={activeTab} onTabChange={onTabChange} visibleTabs={visibleTabs} />
      </div>

      {/* Search Bar and Filter - Only for MDS/Care tabs */}
      {activeTab !== "residents" && activeTab !== "agent" && (
        <>
          <div className="flex-1 min-w-0">
            <div className="relative h-9 border border-solid border-[#D1D2DB] rounded-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D1D2DB]" />
              <Input
                type="text"
                placeholder="Search by resident name, location, or assessment type..."
                value={filters.globalSearch}
                onChange={(e) => onFilterChange("globalSearch", e.target.value)}
                className="h-full w-full pl-10 border-0 text-sm placeholder:text-[#D1D2DB] bg-transparent focus:outline-none focus:ring-0"
              />
            </div>
          </div>

          {multiFacilityMode && facilityOptions && facilityOptions.length >= 1 && (
            <div className="shrink-0">
              <select
                aria-label="Filter by facility"
                value={selectedFacilityId ?? "all"}
                onChange={(e) => onFacilityChange?.(e.target.value)}
                className="h-9 px-2 pr-6 border border-solid border-[#D1D2DB] rounded-md text-xs bg-white text-[#1B3647] cursor-pointer focus:outline-none focus:ring-0"
              >
                <option value="all">All Facilities</option>
                {facilityOptions.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}
          <ActiveFilters
            filters={filters}
            onFilterChange={onFilterChange}
            onClearAllFilters={onClearAllFilters}
          />
          <div className="shrink-0">
            <DateRangeFilter
              value={filters.dueDate}
              onChange={(value) => onFilterChange("dueDate", value)}
            />
          </div>
          <div className="shrink-0">
            <FilterDropdown
              activeTab={activeTab}
              filters={filters}
              onFilterChange={onFilterChange}
              onClearAllFilters={onClearAllFilters}
              onApplyAllFilters={onApplyAllFilters}
              resultCount={resultCount}
              showRecentlyComplete={showRecentlyComplete}
              onShowRecentlyCompleteChange={onShowRecentlyCompleteChange}
              api={api}
            />
          </div>
        </>
      )}

      {/* Search + Filter Button - Residents tab */}
      {activeTab === "residents" && onResidentsSearchChange && (
        <>
          <div className="flex-1 min-w-0">
            <div className="relative h-9 border border-solid border-[#D1D2DB] rounded-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D1D2DB]" />
              <Input
                type="text"
                placeholder="Search by name, room, or ID..."
                value={residentsSearch ?? ""}
                onChange={(e) => onResidentsSearchChange(e.target.value)}
                className="h-full w-full pl-10 border-0 text-sm placeholder:text-[#D1D2DB] bg-transparent focus:outline-none focus:ring-0"
              />
            </div>
          </div>
          {/* NETSOL-11843: only render the toggle when the feature is licensed. */}
          {myAssignmentsAvailable && onMyAssignmentsChange && (
            <div
              className="shrink-0 flex items-center"
              title={
                myAssignmentsLocked
                  ? "Your provider settings restrict the Care Queue to residents assigned to you."
                  : undefined
              }
            >
              <Toggle
                size="sm"
                label="My Assignments"
                labelPlacement="start"
                checked={myAssignments ?? false}
                disabled={myAssignmentsLocked}
                onCheckedChange={(checked) => onMyAssignmentsChange(checked)}
              />
            </div>
          )}
          {multiFacilityMode && facilityOptions && facilityOptions.length >= 1 && (
            <div className="shrink-0">
              <select
                aria-label="Filter by facility"
                value={selectedFacilityId ?? "all"}
                onChange={(e) => onFacilityChange?.(e.target.value)}
                className="h-9 px-2 pr-6 border border-solid border-[#D1D2DB] rounded-md text-xs bg-white text-[#1B3647] cursor-pointer focus:outline-none focus:ring-0"
              >
                <option value="all">All Facilities</option>
                {facilityOptions.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}
          <ActiveResidentsFilters
            selectedRiskLevels={selectedRiskLevels ?? []}
            onRiskLevelsChange={(levels) => onRiskLevelsChange?.(levels)}
            daysSinceReview={daysSinceReview ?? ""}
            onDaysSinceReviewChange={(v) => onDaysSinceReviewChange?.(v)}
            selectedBuilding={selectedBuilding ?? "all"}
            onBuildingChange={(v) => onBuildingChange?.(v)}
            buildingOptions={buildingOptions}
            selectedStation={selectedStation ?? []}
            onStationChange={(v) => onStationChange?.(v)}
            stationOptions={stationOptions}
            showHidden={showHiddenResidents ?? false}
            onShowHiddenChange={(v) => onShowHiddenChange?.(v)}
            onClearAll={() => {
              onRiskLevelsChange?.([]);
              onDaysSinceReviewChange?.("");
              onBuildingChange?.("all");
              onStationChange?.([]);
              onShowHiddenChange?.(false);
            }}
          />
          <div className="shrink-0">
            <ResidentsFilterDropdown
              selectedRiskLevels={selectedRiskLevels ?? []}
              onRiskLevelsChange={(levels) => onRiskLevelsChange?.(levels)}
              daysSinceReview={daysSinceReview ?? ""}
              onDaysSinceReviewChange={(v) => onDaysSinceReviewChange?.(v)}
              selectedBuilding={selectedBuilding ?? "all"}
              onBuildingChange={(v) => onBuildingChange?.(v)}
              buildingOptions={buildingOptions}
              selectedStation={selectedStation ?? []}
              onStationChange={(v) => onStationChange?.(v)}
              stationOptions={stationOptions}
              showHidden={showHiddenResidents ?? false}
              onShowHiddenChange={(v) => onShowHiddenChange?.(v)}
            />
          </div>
          {onSettingsClick && (
            <Button
              variant="outline"
              size="icon"
              onClick={onSettingsClick}
              className="shrink-0 border-primary text-primary hover:bg-primary/10"
              aria-label="Configuration settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </>
      )}
    </div>
  );
}
